import { useState } from "react";
import {
  ChevronRight, ChevronLeft, X,
  Coins, LineChart, Rocket, BookOpen, Target, CheckCircle2, TrendingUp,
  Wallet, PiggyBank, CreditCard, Building2, ShieldCheck, Briefcase, GraduationCap,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import logoSvg from "@/assets/logo.svg";
import { BRAND, INSTRUCTOR_LIST } from "@/lib/brand";
import { INSTRUCTORS } from "@/lib/instructorMapping";

interface Props {
  onComplete: () => void;
}

type PillarKey = "finance" | "economics" | "entrepreneurship";

const PILLAR_META: Record<PillarKey, { icon: typeof Coins; accent: string; glow: string; tint: string }> = {
  finance: {
    icon: Coins,
    accent: "from-cyan-400/80 to-blue-500/80",
    glow: "shadow-[0_0_60px_-20px_rgba(34,211,238,0.55)]",
    tint: "text-cyan-300",
  },
  economics: {
    icon: LineChart,
    accent: "from-blue-400/80 to-indigo-500/80",
    glow: "shadow-[0_0_60px_-20px_rgba(99,102,241,0.55)]",
    tint: "text-blue-300",
  },
  entrepreneurship: {
    icon: Rocket,
    accent: "from-violet-400/80 to-fuchsia-500/80",
    glow: "shadow-[0_0_60px_-20px_rgba(168,85,247,0.55)]",
    tint: "text-violet-300",
  },
};

const PILLAR_BLURBS: Record<PillarKey, string> = {
  finance:
    "Manage, protect, and grow money. Budgeting, saving, credit, investing, taxes, and the everyday decisions that compound.",
  economics:
    "Understand the systems behind money — markets, incentives, inflation, and the forces that shape opportunity.",
  entrepreneurship:
    "Create value and build ownership. Think like a founder and make long-term decisions that shape legacy.",
};

const INSTRUCTOR_BLURBS: Record<PillarKey, string> = {
  finance:
    "Aelyra turns confusing money concepts into practical decisions students can actually use in real life.",
  economics:
    "Caelion teaches students to see the systems behind money — markets, incentives, and opportunity.",
  entrepreneurship:
    "Headmaster Vaelor teaches students to build ownership and think with long-term discipline.",
};

const HOW_STEPS = [
  { icon: BookOpen, title: "Learn the Concept", body: "Short, focused lessons introduce real financial ideas." },
  { icon: Target, title: "Apply the Decision", body: "Practice through scenarios, questions, and real examples." },
  { icon: CheckCircle2, title: "Prove Understanding", body: "Quizzes confirm what you know and what to revisit." },
  { icon: TrendingUp, title: "Build Progress", body: "Unlock lessons, achievements, and hands-on simulators." },
];

const SKILLS = [
  { icon: Wallet, label: "Budgeting" },
  { icon: PiggyBank, label: "Saving Goals" },
  { icon: CreditCard, label: "Credit & Debt" },
  { icon: LineChart, label: "Investing" },
  { icon: ShieldCheck, label: "Scam Defense" },
  { icon: Briefcase, label: "Career Income" },
  { icon: Building2, label: "Real Estate" },
  { icon: Rocket, label: "Entrepreneurship" },
  { icon: GraduationCap, label: "Taxes & Paychecks" },
];

const TOTAL_STEPS = 6;

function StepShell({
  eyebrow,
  title,
  subtitle,
  children,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-5 sm:space-y-6">
      <div className="space-y-2 text-center sm:text-left">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
          <Sparkles className="w-3 h-3 text-primary" />
          <span className="text-[10px] uppercase tracking-[0.18em] text-primary font-semibold">{eyebrow}</span>
        </div>
        <h2 className="font-heading font-bold text-2xl sm:text-3xl text-foreground leading-tight tracking-tight">
          {title}
        </h2>
        {subtitle && (
          <p className="text-sm sm:text-[0.95rem] text-muted-foreground leading-relaxed max-w-xl">
            {subtitle}
          </p>
        )}
      </div>
      {children}
    </div>
  );
}

function WhyStep() {
  return (
    <StepShell eyebrow="Why HeirWise" title="Built before life makes the lesson expensive.">
      <div className="relative rounded-2xl p-5 sm:p-6 bg-gradient-to-br from-primary/10 via-background/40 to-secondary/10 border border-primary/15">
        <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-primary/20 via-transparent to-secondary/20 opacity-40 blur-xl -z-10" />
        <p className="text-[0.95rem] sm:text-base text-foreground/90 leading-relaxed">
          HeirWise helps teens learn the money lessons real life usually teaches too late.
        </p>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
          A place to practice, understand, and build better decisions — before mistakes cost real money.
        </p>
        <p className="mt-4 text-xs italic text-muted-foreground/70 text-right">
          — Built for the next generation.
        </p>
      </div>
    </StepShell>
  );
}

function PillarsStep() {
  return (
    <StepShell
      eyebrow="The Three Pillars"
      title="Money → Systems → Ownership"
      subtitle="Your journey moves from managing money, to understanding systems, to building ownership."
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {(Object.keys(PILLAR_META) as PillarKey[]).map((p) => {
          const meta = PILLAR_META[p];
          const Icon = meta.icon;
          const instructor = BRAND.instructors[p];
          return (
            <div
              key={p}
              className={`relative rounded-xl p-4 bg-card/60 border border-border/60 hover:border-primary/40 transition-all ${meta.glow}`}
            >
              <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${meta.accent} flex items-center justify-center mb-3`}>
                <Icon className="w-4.5 h-4.5 text-white" strokeWidth={2.2} />
              </div>
              <div className="font-heading font-bold text-sm text-foreground mb-1">{BRAND.pillars[p]}</div>
              <p className="text-[11px] text-muted-foreground leading-relaxed mb-3">{PILLAR_BLURBS[p]}</p>
              <div className={`text-[10px] uppercase tracking-wider font-semibold ${meta.tint}`}>
                Guide · {instructor.name.split(" ")[0]}
              </div>
            </div>
          );
        })}
      </div>
    </StepShell>
  );
}

function GuidesStep() {
  return (
    <StepShell
      eyebrow="The Luminar Collective"
      title="Meet Your Guides"
      subtitle="Three instructors will walk with you through finance, economics, and entrepreneurship."
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {INSTRUCTOR_LIST.map((ins) => {
          const meta = PILLAR_META[ins.pillar];
          const img = INSTRUCTORS[ins.pillar].image;
          return (
            <div
              key={ins.name}
              className={`relative rounded-xl overflow-hidden bg-card/60 border border-border/60 hover:border-primary/40 transition-all ${meta.glow}`}
            >
              <div className="aspect-[4/5] w-full overflow-hidden bg-muted/20">
                <img src={img} alt={ins.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent pointer-events-none" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <div className={`text-[10px] uppercase tracking-wider font-semibold ${meta.tint} mb-0.5`}>
                  {ins.title}
                </div>
                <div className="font-heading font-bold text-sm text-foreground leading-tight">{ins.name}</div>
                <p className="hidden sm:block mt-1.5 text-[11px] text-muted-foreground leading-snug">
                  {INSTRUCTOR_BLURBS[ins.pillar]}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </StepShell>
  );
}

function HowStep() {
  return (
    <StepShell
      eyebrow="The Method"
      title="Learn. Apply. Practice. Grow."
      subtitle="A simple loop that builds real financial understanding over time."
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {HOW_STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={s.title}
              className="relative rounded-xl p-4 bg-card/60 border border-border/60 flex gap-3 items-start"
            >
              <div className="w-9 h-9 rounded-lg bg-primary/15 border border-primary/25 flex items-center justify-center shrink-0">
                <Icon className="w-4.5 h-4.5 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] font-bold text-primary/70">0{i + 1}</span>
                  <span className="font-heading font-bold text-sm text-foreground">{s.title}</span>
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            </div>
          );
        })}
      </div>
    </StepShell>
  );
}

function BuildStep() {
  return (
    <StepShell
      eyebrow="What You Will Build"
      title="Real-world money skills, before adulthood."
      subtitle="A preview of the skills you'll develop on the HeirWise journey."
    >
      <div className="grid grid-cols-3 sm:grid-cols-3 gap-2">
        {SKILLS.map((s) => {
          const Icon = s.icon;
          return (
            <div
              key={s.label}
              className="rounded-lg p-3 bg-card/50 border border-border/60 hover:border-primary/40 transition-colors flex flex-col items-center text-center gap-1.5"
            >
              <Icon className="w-4 h-4 text-primary" />
              <span className="text-[10.5px] sm:text-xs text-foreground/85 font-medium leading-tight">
                {s.label}
              </span>
            </div>
          );
        })}
      </div>
    </StepShell>
  );
}

function StartStep() {
  return (
    <StepShell
      eyebrow="Begin"
      title="Your journey starts now."
      subtitle="Start with Financial Foundations and build mastery one decision at a time."
    >
      <div className="rounded-2xl p-5 sm:p-6 bg-gradient-to-br from-primary/15 via-background/40 to-secondary/15 border border-primary/20">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-glow shrink-0">
            <Rocket className="w-5 h-5 text-white" strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-primary mb-0.5">
              First Lesson
            </div>
            <div className="font-heading font-bold text-base text-foreground leading-tight">
              Financial Foundations
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Guided by Aelyra Solari · Finance pillar
            </p>
          </div>
        </div>
      </div>
    </StepShell>
  );
}

const STEPS = [WhyStep, PillarsStep, GuidesStep, HowStep, BuildStep, StartStep];

export default function OnboardingOverlay({ onComplete }: Props) {
  const [step, setStep] = useState(0);
  const isLast = step === STEPS.length - 1;
  const Current = STEPS[step];

  return (
    <div className="fixed inset-0 z-[200] overflow-y-auto bg-background/95 backdrop-blur-xl">
      {/* Ambient gradient glow */}
      <div className="pointer-events-none fixed inset-0 -z-0">
        <div className="absolute -top-32 -left-32 w-[40rem] h-[40rem] rounded-full bg-primary/15 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-[40rem] h-[40rem] rounded-full bg-secondary/15 blur-3xl" />
      </div>

      <div className="relative min-h-full flex items-center justify-center p-4 py-8">
        <div className="w-full max-w-2xl space-y-4">
          {/* Top bar: logo + skip */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src={logoSvg} alt="HeirWise" className="w-7 h-7" />
              <span className="font-heading font-bold text-sm text-foreground tracking-wide">
                {BRAND.productName}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onComplete}
              className="text-muted-foreground hover:text-foreground"
            >
              Skip <X className="w-3 h-3 ml-1" />
            </Button>
          </div>

          {/* Progress: numbered + bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <span>
                Step <span className="text-primary font-semibold">{step + 1}</span> of {TOTAL_STEPS}
              </span>
              <span>{Math.round(((step + 1) / TOTAL_STEPS) * 100)}%</span>
            </div>
            <div className="h-1 rounded-full bg-muted/30 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary via-primary to-secondary transition-all duration-500 shadow-[0_0_12px_rgba(99,102,241,0.6)]"
                style={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }}
              />
            </div>
          </div>

          {/* Main card */}
          <div className="relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-secondary/20 opacity-60 blur-2xl -z-10" />
            <div className="glass-card border border-white/10 p-6 sm:p-8 animate-fade-in" key={step}>
              <Current />

              {/* CTAs */}
              <div className="mt-7 flex items-center justify-between gap-3">
                <Button
                  variant="outline"
                  className="border-border/60 bg-card/40 text-muted-foreground hover:text-foreground hover:bg-card/60 disabled:opacity-30"
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>

                <div className="hidden sm:flex items-center gap-1.5">
                  {STEPS.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setStep(i)}
                      aria-label={`Go to step ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === step
                          ? "bg-primary w-8 shadow-[0_0_10px_rgba(99,102,241,0.7)]"
                          : i < step
                          ? "bg-primary/50 w-2"
                          : "bg-muted w-2 hover:bg-muted/70"
                      }`}
                    />
                  ))}
                </div>

                <Button
                  className="bg-gradient-to-r from-primary to-secondary text-white shadow-glow hover:shadow-[0_0_24px_rgba(99,102,241,0.6)] hover:opacity-95 transition-all"
                  onClick={() => {
                    if (isLast) onComplete();
                    else setStep((s) => s + 1);
                  }}
                >
                  {isLast ? "Start Learning" : "Continue"}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>

          <p className="text-center text-muted-foreground/40 text-[10px] tracking-wider uppercase">
            {BRAND.collective.nameTitleCase} · {BRAND.tagline}
          </p>
        </div>
      </div>
    </div>
  );
}
